import { useMyUser, useProject, useProjectId } from '@consigli/hooks';
import {
  BuildingPackageType,
  DocumentPackageType,
  LayoutType,
  MeetingSecretaryType,
  OrganizationType,
  type Project,
  ProjectAccess,
  ProjectRole,
  QaAgentType,
  ServiceNameType,
  ServicePermissionName,
  SolutionViewerType,
  StructuralType,
} from '@consigli/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isAecomOrganization } from '@/util/aecom';
import { filterBidReviewService } from '@/util/bid-review-access';
import { useDemoMode } from '@/util/demo-mode';
import {
  contractorServicesOrder,
  developerServicesOrder,
  serviceConfig,
  SHOW_BETA_BADGES,
  superUserDeveloperServicesOrder,
} from '@/util/service-config';

export type ServiceData = {
  serviceName: ServiceNameType;
  servicePermissionName?: ServicePermissionName;
  active?: boolean;
  isBeta?: boolean;
  icon: string;
  tooltip?: string;
  title: string;
  newServiceRoute?: string;
  serviceRoute?: string;
  serviceId?:
    | DocumentPackageType
    | LayoutType
    | BuildingPackageType
    | MeetingSecretaryType
    | SolutionViewerType
    | QaAgentType
    | StructuralType;
  externalToSolutionViewer?: boolean;
  externalUrl?: string;
  openInSameWindow?: boolean;
};

/**
 * Which service cards the current user sees for the current project, in order.
 *
 * Everything here was lifted verbatim out of `pages/service-list/service-list-page.tsx`
 * so a second page can derive the same list without copying it. The flat list is
 * deliberate: the applied/available split belongs to whichever view renders it,
 * not here.
 *
 * `project` and `isLoading` come back because callers render the project name and
 * a loading state from the same `useProject` call this hook already makes.
 */
export const useProjectServices = (): {
  services: ServiceData[];
  isLoading: boolean;
  project: Project | undefined;
} => {
  const projectId = useProjectId();
  const { t } = useTranslation();
  const { project, isLoading } = useProject(projectId);
  const { user } = useMyUser();
  const { adminFeaturesEnabled } = useDemoMode();

  const qaAgentUrl = import.meta.env.VITE_QA_AGENT_URL;

  const isAecomOrg = useMemo(
    () => isAecomOrganization(project?.organizationName),
    [project?.organizationName],
  );

  const generateServiceData = useCallback(
    (order: string[]): ServiceData[] => {
      // `[]` is truthy, so availableServices is only consulted when
      // effectiveServices is undefined — not when it is empty.
      const availableServices = project?.effectiveServices || project?.availableServices || [];
      // Superusers can see all services, regardless of org's available_services
      let filteredServices = user.isSuperuser
        ? serviceConfig
        : serviceConfig.filter(
            (service) =>
              service.serviceName === ServiceNameType.SOLUTION_VIEWER ||
              service.serviceName === ServiceNameType.DATA_CENTER ||
              service.serviceName === ServiceNameType.WASTEWATER_TREATMENT ||
              // The backend does not send BID_REVIEW in effective_services; it is
              // the Offer service as a second card, so it rides on Offer's
              // availability. filterBidReviewService below picks which renders.
              // TODO: show Bid Review instead of Offer once the service is
              // confirmed ready for all business lines.
              (service.serviceName === ServiceNameType.BID_REVIEW &&
                availableServices.includes(ServiceNameType.OFFER)) ||
              availableServices.includes(service.serviceName),
          );

      if (isAecomOrg && !user.isSuperuser) {
        filteredServices = filteredServices.filter(
          (service) => service.serviceName !== ServiceNameType.TENANT_REQUIREMENTS,
        );
      }

      // Solution Viewer access is controlled by user-level flag, not service permissions
      if (!user.solutionViewerAccess && !user.isSuperuser) {
        filteredServices = filteredServices.filter(
          (service) => service.serviceName !== ServiceNameType.SOLUTION_VIEWER,
        );
      }

      // Data Center is a dedicated user-level toggle, separate from Solution Viewer access
      if (!user.solutionViewerDatacenterAccess && !user.isSuperuser) {
        filteredServices = filteredServices.filter(
          (service) => service.serviceName !== ServiceNameType.DATA_CENTER,
        );
      }

      // Wastewater treatment is decided the same way, by its own user-level toggle
      if (!user.solutionViewerWaterTreatmentAccess && !user.isSuperuser) {
        filteredServices = filteredServices.filter(
          (service) => service.serviceName !== ServiceNameType.WASTEWATER_TREATMENT,
        );
      }

      // The Environment Report Agent is superusers-only while it is being built,
      // whatever the org's services or the user's permissions say; INS-750 lifts
      // that by removing `user.isSuperuser` below. It also has no in-app route to
      // fall back on, so with no URL configured for this environment the card
      // would go nowhere. Drop it in either case.
      filteredServices = filteredServices.filter(
        (service) =>
          service.serviceName !== ServiceNameType.ENVIRONMENT_REPORT_AGENT ||
          (user.isSuperuser && 'externalUrl' in service && !!service.externalUrl),
      );

      // The Lightning Designer has no in-app route either, so drop it where no
      // URL is configured for this environment. Otherwise the org's services and
      // the user's permissions decide, as for any other card.
      filteredServices = filteredServices.filter(
        (service) =>
          service.serviceName !== ServiceNameType.LIGHTNING_DESIGNER ||
          ('externalUrl' in service && !!service.externalUrl),
      );

      filteredServices = filterBidReviewService(filteredServices, projectId);

      const services: ServiceData[] = filteredServices.map((service) => {
        // Model Review Agent opens the standalone frontend when a URL is configured for
        // this environment; otherwise it falls back to the in-app form.
        const useQaAgentFrontend = service.serviceName === ServiceNameType.QA_AGENT && !!qaAgentUrl;

        return {
          serviceName: service.serviceName,
          servicePermissionName: service.servicePermissionName,
          // Use alwaysActive flag if present, otherwise check project's activeServices
          // activeServices holds ServiceName ('due-diligence'), not ServiceNameType.
          active:
            service.alwaysActive ??
            (service.serviceType ? project?.activeServices?.includes(service.serviceType) : false),
          isBeta: SHOW_BETA_BADGES && service.isBeta,
          icon: service.icon,
          tooltip: t(service.tooltipKey),
          title: t(service.titleKey),
          newServiceRoute: service.newServiceRoute,
          serviceRoute: service.serviceRoute,
          serviceId: service.serviceId,
          externalToSolutionViewer: service.externalToSolutionViewer,
          externalUrl: useQaAgentFrontend ? qaAgentUrl : service.externalUrl,
          openInSameWindow: useQaAgentFrontend ? true : service.openInSameWindow,
        };
      });

      // indexOf returns -1, so services absent from `order` sort FIRST, not last.
      // Live for up to 9 cards; fixing it reshuffles them, so it needs its own PR.
      return services.sort((a, b) => order.indexOf(a.serviceName) - order.indexOf(b.serviceName));
    },
    [
      project?.effectiveServices,
      project?.availableServices,
      project?.activeServices,
      t,
      user.isSuperuser,
      user.solutionViewerAccess,
      user.solutionViewerDatacenterAccess,
      user.solutionViewerWaterTreatmentAccess,
      isAecomOrg,
      qaAgentUrl,
      projectId,
    ],
  );

  const developerOrder = useMemo(
    () =>
      generateServiceData(
        adminFeaturesEnabled ? superUserDeveloperServicesOrder : developerServicesOrder,
      ),
    [generateServiceData, adminFeaturesEnabled],
  );

  // due diligence is irrelevant for contractors
  const contractorOrder = useMemo(
    () =>
      // Superusers get the *developer* order here; superUserContractorServicesOrder
      // exists but is unused. Deliberate or not, changing it is a product call.
      generateServiceData(
        adminFeaturesEnabled ? superUserDeveloperServicesOrder : contractorServicesOrder,
      ),
    [generateServiceData, adminFeaturesEnabled],
  );

  const filteredServices: ServiceData[] = useMemo(() => {
    let services;
    if (
      project?.organizationType === OrganizationType.CONTRACTOR ||
      project?.organizationType === OrganizationType.SUBCONTRACTOR
    ) {
      services = contractorOrder;
    } else {
      services = developerOrder;
    }

    if (user.isSuperuser || user.isOrgAdmin) {
      return services;
    }
    const currentProjectAccess = user.projectAccess.find(
      (access: ProjectAccess) => access.id === projectId,
    );
    if (currentProjectAccess) {
      if (currentProjectAccess.role === ProjectRole.ADMIN) {
        return services;
      }
      if (currentProjectAccess.role === ProjectRole.USER) {
        const availableServices: ServiceData[] = [];
        availableServices.push(
          ...services.filter(
            (service) =>
              service.serviceName === ServiceNameType.SOLUTION_VIEWER ||
              service.serviceName === ServiceNameType.DATA_CENTER ||
              service.serviceName === ServiceNameType.WASTEWATER_TREATMENT ||
              (service.servicePermissionName &&
                currentProjectAccess.services.includes(service.servicePermissionName)),
          ),
        );
        return availableServices;
      }
    }
    return [];
  }, [
    project?.organizationType,
    user.isSuperuser,
    user.isOrgAdmin,
    user.projectAccess,
    contractorOrder,
    developerOrder,
    projectId,
  ]);

  return { services: filteredServices, isLoading, project };
};
